import { useState, useCallback, useMemo, useRef } from "react";
import { cn } from "../lib/utils";
import { useAssets } from "../hooks/useAssets";
import { useCurrentOwner } from "../hooks/useCurrentOwner";
import { AssetCard } from "../components/ui/AssetCard";
import { FolderCard } from "../components/ui/FolderCard";
import { AssetUploadDialog } from "../components/ui/AssetUploadDialog";
import { AssetDetailSheet } from "../components/ui/AssetDetailSheet";
import { NewFolderDialog } from "../components/ui/NewFolderDialog";
import { ImageLightbox } from "../components/ui/ImageLightbox";
import { PdfViewer } from "../components/ui/PdfViewer";
import { EmptyState } from "../components/ui/EmptyState";
import { MoveAssetDialog } from "../components/ui/MoveAssetDialog";
import { IconFolder, IconSquareGrid, IconBulletList } from "nucleo-glass";
import { NucleoIcon } from "../components/ui/NucleoIcon";
import { HugeiconsIcon } from "@hugeicons/react";
import { Add01Icon, ArrowDown01Icon, ArrowLeft01Icon, Folder01Icon } from "@hugeicons/core-free-icons";
import { Search01Icon } from "@hugeicons/core-free-icons";
import { ASSET_CATEGORIES, formatFileSize, isPreviewableImage, isPreviewablePdf, inferCategoryFromFileType } from "../lib/assetUtils";
import type { AssetCategory } from "../lib/assetUtils";
import type { Owner } from "../data/types";
import { OWNER_OPTIONS } from "../lib/owner";

export function AssetsPage() {
  const {
    assets,
    allAssets,
    stats,
    filters,
    viewMode,
    isLoading,
    isSearching,
    setFilters,
    resetFilters,
    setViewMode,
    createAsset,
    updateAsset,
    replaceFile,
    restoreVersion,
    removeAsset,

    // Folders
    folders,
    foldersInView,
    currentFolderId,
    breadcrumbs,
    navigateToFolder,
    navigateUp,
    createFolder,
    renameFolder,
    removeFolder,
    moveAssetToFolder,
    getFolderCount,
  } = useAssets();

  const { owner: currentOwner } = useCurrentOwner();

  const [importOpen, setImportOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmDeleteFolder, setConfirmDeleteFolder] = useState<string | null>(null);
  const [confirmDeleteAsset, setConfirmDeleteAsset] = useState<string | null>(null);
  const [moveAssetId, setMoveAssetId] = useState<string | null>(null);

  // ── Page-level drag-and-drop state ──
  const [isPageDragOver, setIsPageDragOver] = useState(false);
  const [dropImporting, setDropImporting] = useState(false);
  const [dropProgress, setDropProgress] = useState<{ done: number; total: number } | null>(null);
  const dragCounterRef = useRef(0);

  // ── Image lightbox state ──
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // ── PDF viewer state ──
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [pdfViewerTitle, setPdfViewerTitle] = useState("");
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);

  const selectedAsset = useMemo(() => {
    if (!selectedAssetId) return null;
    const found = allAssets.find((a) => a.id === selectedAssetId);
    if (!found) return null;
    return { ...found, currentVersion: undefined as number | undefined };
  }, [selectedAssetId, allAssets]);

  const handleSelectAsset = useCallback((id: string) => {
    const asset = allAssets.find((a) => a.id === id);
    if (!asset) return;

    // Full-screen image lightbox
    if (isPreviewableImage(asset.fileType) && asset.fileUrl) {
      setLightboxSrc(asset.fileUrl);
      setLightboxAlt(asset.title);
      setLightboxOpen(true);
      return;
    }

    // Full-screen PDF viewer
    if (isPreviewablePdf(asset.fileType) && asset.fileUrl) {
      setPdfViewerUrl(asset.fileUrl);
      setPdfViewerTitle(asset.title);
      setPdfViewerOpen(true);
      return;
    }

    // Everything else → detail sheet
    setSelectedAssetId(id);
    setDetailOpen(true);
  }, [allAssets]);

  const handleDownload = useCallback((id: string) => {
    const asset = allAssets.find((a) => a.id === id);
    if (!asset?.fileUrl) return;
    const link = document.createElement("a");
    link.href = asset.fileUrl;
    link.download = asset.fileName ?? "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [allAssets]);

  const handleEditAsset = useCallback((id: string) => {
    setSelectedAssetId(id);
    setDetailOpen(true);
  }, []);

  const handleMoveAsset = useCallback((id: string) => {
    setMoveAssetId(id);
  }, []);

  const handleConfirmMove = useCallback(async (folderId: string | undefined) => {
    if (!moveAssetId) return;
    await moveAssetToFolder(moveAssetId, folderId);
    setMoveAssetId(null);
  }, [moveAssetId, moveAssetToFolder]);

  const handleDeleteAsset = useCallback((id: string) => {
    setConfirmDeleteAsset(id);
  }, []);

  const confirmAssetDelete = useCallback(async () => {
    if (!confirmDeleteAsset) return;
    await removeAsset(confirmDeleteAsset);
    setConfirmDeleteAsset(null);
  }, [confirmDeleteAsset, removeAsset]);

  const handleDropAssetOnFolder = useCallback(async (assetId: string, folderId: string) => {
    await moveAssetToFolder(assetId, folderId);
  }, [moveAssetToFolder]);

  // ── Page-level drag-and-drop handlers ──
  const handlePageDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes("Files")) {
      setIsPageDragOver(true);
    }
  }, []);

  const handlePageDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handlePageDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsPageDragOver(false);
    }
  }, []);

  const handlePageDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsPageDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    setDropImporting(true);
    setDropProgress({ done: 0, total: files.length });

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        const nameWithoutExt = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
        const category = inferCategoryFromFileType(file.type);

        await createAsset({
          title: nameWithoutExt,
          description: "",
          category,
          owner: currentOwner,
          file,
        });

        setDropProgress({ done: i + 1, total: files.length });
      }
    } finally {
      // Brief delay so user sees "done" state
      setTimeout(() => {
        setDropImporting(false);
        setDropProgress(null);
      }, 600);
    }
  }, [createAsset, currentOwner]);

  const handleImport = useCallback(async (data: {
    title: string;
    description: string;
    category: AssetCategory;
    owner: Owner;
    file?: File;
  }) => {
    await createAsset(data);
  }, [createAsset]);

  const handleCreateFolder = useCallback(async (name: string, owner: Owner) => {
    await createFolder(name, owner);
  }, [createFolder]);

  const handleDeleteFolder = useCallback(async (id: string) => {
    setConfirmDeleteFolder(id);
  }, []);

  const confirmFolderDelete = useCallback(async () => {
    if (!confirmDeleteFolder) return;
    await removeFolder(confirmDeleteFolder);
    setConfirmDeleteFolder(null);
  }, [confirmDeleteFolder, removeFolder]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-[14px] text-[var(--color-text-muted)]">Loading assets…</p>
      </div>
    );
  }

  const hasActiveFilters = filters.category !== "all" || filters.owner !== "all" || filters.search !== "";
  const isInSubfolder = currentFolderId !== null;
  const showFolders = !isSearching && foldersInView.length > 0;
  const totalItems = assets.length + (isSearching ? 0 : foldersInView.length);

  return (
    <div
      className="h-full flex flex-col overflow-hidden relative"
      onDragEnter={handlePageDragEnter}
      onDragOver={handlePageDragOver}
      onDragLeave={handlePageDragLeave}
      onDrop={handlePageDrop}
    >
      {/* ── Page drag-and-drop overlay ── */}
      {(isPageDragOver || dropImporting) && (
        <div
          className={cn(
            "absolute inset-0 z-50 flex items-center justify-center",
            "bg-[var(--color-bg-canvas)]/80 backdrop-blur-[4px]",
            "border-2 border-dashed rounded-[var(--radius-lg)]",
            "transition-all duration-[180ms]",
            isPageDragOver && !dropImporting
              ? "border-[var(--color-accent)] bg-[var(--color-accent-900)]/10"
              : "border-[var(--color-border-stroke)]",
          )}
          style={{ pointerEvents: dropImporting ? "none" : "auto" }}
        >
          <div className="flex flex-col items-center gap-[var(--spacing-3)]">
            {dropImporting ? (
              <>
                <div className="w-[48px] h-[48px] flex items-center justify-center">
                  <div className="w-[32px] h-[32px] rounded-full border-2 border-[var(--color-accent)] border-t-transparent animate-spin" />
                </div>
                <p className="text-[14px] font-medium text-[var(--color-text-primary)]">
                  Importing {dropProgress ? `${dropProgress.done}/${dropProgress.total}` : "…"}
                </p>
                {dropProgress && (
                  <div className="w-[200px] h-[4px] rounded-full bg-[var(--color-bg-hover)] overflow-hidden">
                    <div
                      className="h-full bg-[var(--color-accent)] rounded-full transition-all duration-[240ms]"
                      style={{ width: `${(dropProgress.done / dropProgress.total) * 100}%` }}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="relative w-[48px] h-[48px] flex items-center justify-center">
                  <div className="absolute inset-0 rounded-[var(--radius-lg)] bg-gradient-to-br from-[var(--color-accent-900)] to-[var(--color-bg-hover)] border border-[var(--color-accent)]/30" />
                  <div className="relative z-10 text-[var(--color-accent-300)]">
                    <HugeiconsIcon icon={Add01Icon} size={24} />
                  </div>
                </div>
                <p className="text-[14px] font-medium text-[var(--color-text-primary)]">
                  Drop files to import
                </p>
                <p className="text-[12px] text-[var(--color-text-muted)]">
                  Files will be added to the current folder
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className={cn(
        "shrink-0 flex items-center gap-[var(--spacing-3)] flex-wrap",
        "px-[var(--spacing-4)] py-[var(--spacing-3)]",
        "border-b border-[var(--color-border-stroke)]",
      )}>
        {/* Back button when inside a folder */}
        {isInSubfolder && (
          <button
            onClick={navigateUp}
            className={cn(
              "h-[32px] w-[32px] flex items-center justify-center",
              "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
              "bg-[var(--color-bg-surface)] hover:bg-[var(--color-bg-hover)]",
              "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
              "transition-colors duration-[120ms] cursor-pointer",
            )}
            aria-label="Go back"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
          </button>
        )}

        {/* Search */}
        <div className={cn(
          "flex items-center gap-[var(--spacing-2)] h-[32px] w-[240px]",
          "rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
          "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)]",
          "focus-within:border-[var(--color-accent-focus)]",
          "transition-colors duration-[var(--duration-fast)]",
        )}>
          <HugeiconsIcon
            icon={Search01Icon}
            size={14}
            className="shrink-0 text-[var(--color-icon)] opacity-60"
          />
          <input
            type="text"
            placeholder={isSearching ? "Searching all folders…" : "Search assets…"}
            value={filters.search}
            onChange={(e) => setFilters({ search: e.target.value })}
            className={cn(
              "flex-1 h-full min-w-0 bg-transparent border-none outline-none shadow-none ring-0",
              "text-[13px] text-[var(--color-text-primary)]",
              "placeholder:text-[var(--color-text-muted)]",
            )}
          />
        </div>

        {/* Category filter */}
        <div className="relative">
          <select
            value={filters.category}
            onChange={(e) => setFilters({ category: e.target.value as AssetCategory | "all" })}
            className={cn(
              "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
              "bg-[var(--color-bg-canvas)] pl-[var(--spacing-3)] pr-[var(--spacing-8)] text-[13px] text-[var(--color-text-primary)]",
              "appearance-none cursor-pointer transition-colors duration-[var(--duration-fast)]",
              "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
            )}
          >
            <option value="all">All categories</option>
            {ASSET_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={14}
            className="absolute right-[var(--spacing-2)] top-1/2 -translate-y-1/2 pointer-events-none text-[var(--color-icon)]"
          />
        </div>

        {/* Owner filter */}
        <div className="relative">
          <select
            value={filters.owner}
            onChange={(e) => setFilters({ owner: e.target.value as Owner | "all" })}
            className={cn(
              "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
              "bg-[var(--color-bg-canvas)] pl-[var(--spacing-3)] pr-[var(--spacing-8)] text-[13px] text-[var(--color-text-primary)]",
              "appearance-none cursor-pointer transition-colors duration-[var(--duration-fast)]",
              "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
            )}
          >
            <option value="all">All owners</option>
            {OWNER_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={14}
            className="absolute right-[var(--spacing-2)] top-1/2 -translate-y-1/2 pointer-events-none text-[var(--color-icon)]"
          />
        </div>

        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="text-[12px] font-medium text-[var(--color-accent)] hover:text-[var(--color-accent-300)] transition-colors cursor-pointer"
          >
            Clear filters
          </button>
        )}

        <div className="flex-1" />

        {/* Stats */}
        <span className="text-[12px] text-[var(--color-text-muted)] tabular-nums shrink-0">
          {totalItems} item{totalItems !== 1 ? "s" : ""}
          {stats.totalSizeBytes > 0 && ` · ${formatFileSize(stats.totalSizeBytes)}`}
        </span>

        {/* View toggle */}
        <div className="flex items-center rounded-[var(--radius-md)] border border-[var(--color-border-stroke)] overflow-hidden">
          <button
            onClick={() => setViewMode("grid")}
            className={cn(
              "h-[30px] w-[32px] flex items-center justify-center",
              "transition-colors duration-[120ms] cursor-pointer",
              viewMode === "grid"
                ? "bg-[var(--color-bg-hover)] text-[var(--color-text-primary)]"
                : "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
            )}
            aria-label="Grid view"
          >
            <IconSquareGrid size={14} />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={cn(
              "h-[30px] w-[32px] flex items-center justify-center",
              "border-l border-[var(--color-border-stroke)]",
              "transition-colors duration-[120ms] cursor-pointer",
              viewMode === "list"
                ? "bg-[var(--color-bg-hover)] text-[var(--color-text-primary)]"
                : "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
            )}
            aria-label="List view"
          >
            <IconBulletList size={14} />
          </button>
        </div>

        {/* New Folder button */}
        <button
          onClick={() => setNewFolderOpen(true)}
          className={cn(
            "flex items-center gap-[var(--spacing-1_5)] h-[32px] px-[var(--spacing-3)]",
            "rounded-[var(--radius-md)] text-[13px] font-medium",
            "text-[var(--color-text-primary)]",
            "border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-surface)] hover:bg-[var(--color-bg-hover)]",
            "transition-all duration-[120ms] cursor-pointer",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2",
          )}
        >
          <HugeiconsIcon icon={Folder01Icon} size={14} />
          New Folder
        </button>

        {/* Import button */}
        <button
          onClick={() => setImportOpen(true)}
          className={cn(
            "flex items-center gap-[var(--spacing-1_5)] h-[32px] px-[var(--spacing-3)]",
            "rounded-[var(--radius-md)] text-[13px] font-medium text-white",
            "bg-gradient-to-b from-[var(--color-accent-400)] to-[var(--color-accent-600)]",
            "hover:from-[var(--color-accent-300)] hover:to-[var(--color-accent-500)]",
            "transition-all duration-[120ms] cursor-pointer",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2",
          )}
        >
          <HugeiconsIcon icon={Add01Icon} size={14} />
          Import
        </button>
      </div>

      {/* ── Breadcrumbs ── */}
      {isInSubfolder && !isSearching && (
        <div className={cn(
          "shrink-0 flex items-center gap-[var(--spacing-1)] px-[var(--spacing-4)] py-[var(--spacing-2)]",
          "border-b border-[var(--color-border-stroke)] bg-[var(--color-bg-canvas)]",
        )}>
          {breadcrumbs.map((crumb, idx) => (
            <div key={crumb.id ?? "root"} className="flex items-center gap-[var(--spacing-1)]">
              {idx > 0 && (
                <span className="text-[12px] text-[var(--color-text-muted)]">/</span>
              )}
              <button
                onClick={() => navigateToFolder(crumb.id)}
                className={cn(
                  "text-[13px] px-[var(--spacing-1)] py-[var(--spacing-0_5)] rounded-[var(--radius-sm)]",
                  "transition-colors duration-[120ms] cursor-pointer",
                  idx === breadcrumbs.length - 1
                    ? "text-[var(--color-text-primary)] font-medium"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
                )}
              >
                {crumb.name}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto p-[var(--spacing-4)]">
        {allAssets.length === 0 && foldersInView.length === 0 && !isInSubfolder ? (
          <EmptyState
            title="No assets yet"
            description="Import branding, documents, decks, images, and more to your team library."
            icon={
              <div className="relative w-[48px] h-[48px] flex items-center justify-center">
                <div className="absolute inset-0 rounded-[var(--radius-lg)] bg-gradient-to-br from-[var(--color-accent-900)] to-[var(--color-bg-hover)] border border-[var(--color-border-stroke)] backdrop-blur-[8px]" />
                <div className="relative z-10 text-[var(--color-accent-300)]">
                  <NucleoIcon icon={<IconFolder size={24} />} />
                </div>
              </div>
            }
          />
        ) : totalItems === 0 && hasActiveFilters ? (
          <EmptyState
            title="No matching assets"
            description="Try adjusting your filters or search query."
            variant="search"
          />
        ) : totalItems === 0 && isInSubfolder ? (
          <EmptyState
            title="Empty folder"
            description="Import files or create subfolders to organize your assets."
            icon={
              <div className="relative w-[48px] h-[48px] flex items-center justify-center">
                <div className="absolute inset-0 rounded-[var(--radius-lg)] bg-gradient-to-br from-[var(--color-accent-900)] to-[var(--color-bg-hover)] border border-[var(--color-border-stroke)] backdrop-blur-[8px]" />
                <div className="relative z-10 text-[var(--color-accent-300)]">
                  <NucleoIcon icon={<IconFolder size={24} />} />
                </div>
              </div>
            }
          />
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-[var(--spacing-4)]">
            {/* Folders first */}
            {showFolders && foldersInView.map((folder, idx) => (
              <div
                key={folder.id}
                className="animate-list-item-in"
                style={{ animationDelay: `${Math.min(idx, 15) * 30}ms` }}
              >
                <FolderCard
                  id={folder.id}
                  name={folder.name}
                  owner={folder.owner}
                  itemCount={getFolderCount(folder.id)}
                  viewMode="grid"
                  onOpen={navigateToFolder}
                  onRename={renameFolder}
                  onDelete={handleDeleteFolder}
                  onDropAsset={handleDropAssetOnFolder}
                />
              </div>
            ))}
            {/* Then assets */}
            {assets.map((asset, idx) => (
              <div
                key={asset.id}
                className="animate-list-item-in"
                style={{ animationDelay: `${Math.min(idx + foldersInView.length, 15) * 30}ms` }}
              >
                <AssetCard
                  id={asset.id}
                  title={asset.title}
                  description={asset.description}
                  category={asset.category}
                  owner={asset.owner}
                  fileName={asset.fileName}
                  fileType={asset.fileType}
                  fileSize={asset.fileSize}
                  fileUrl={asset.fileUrl}
                  createdAt={asset.createdAt}
                  viewMode="grid"
                  onClick={handleSelectAsset}
                  onDownload={handleDownload}
                  onEdit={handleEditAsset}
                  onMove={handleMoveAsset}
                  onDelete={handleDeleteAsset}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-[var(--spacing-1)]">
            {/* Folders first */}
            {showFolders && foldersInView.map((folder, idx) => (
              <div
                key={folder.id}
                className="animate-list-item-in"
                style={{ animationDelay: `${Math.min(idx, 15) * 20}ms` }}
              >
                <FolderCard
                  id={folder.id}
                  name={folder.name}
                  owner={folder.owner}
                  itemCount={getFolderCount(folder.id)}
                  viewMode="list"
                  onOpen={navigateToFolder}
                  onRename={renameFolder}
                  onDelete={handleDeleteFolder}
                  onDropAsset={handleDropAssetOnFolder}
                />
              </div>
            ))}
            {/* Then assets */}
            {assets.map((asset, idx) => (
              <div
                key={asset.id}
                className="animate-list-item-in"
                style={{ animationDelay: `${Math.min(idx + foldersInView.length, 15) * 20}ms` }}
              >
                <AssetCard
                  id={asset.id}
                  title={asset.title}
                  description={asset.description}
                  category={asset.category}
                  owner={asset.owner}
                  fileName={asset.fileName}
                  fileType={asset.fileType}
                  fileSize={asset.fileSize}
                  fileUrl={asset.fileUrl}
                  createdAt={asset.createdAt}
                  viewMode="list"
                  onClick={handleSelectAsset}
                  onDownload={handleDownload}
                  onEdit={handleEditAsset}
                  onMove={handleMoveAsset}
                  onDelete={handleDeleteAsset}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Dialogs ── */}
      <MoveAssetDialog
        open={moveAssetId !== null}
        onOpenChange={(open) => { if (!open) setMoveAssetId(null); }}
        assetTitle={allAssets.find((a) => a.id === moveAssetId)?.title ?? ""}
        currentFolderId={allAssets.find((a) => a.id === moveAssetId)?.folderId ?? null}
        folders={folders}
        onMove={handleConfirmMove}
      />

      <AssetUploadDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onSubmit={handleImport}
      />

      <NewFolderDialog
        open={newFolderOpen}
        onOpenChange={setNewFolderOpen}
        onSubmit={handleCreateFolder}
      />

      <AssetDetailSheet
        asset={selectedAsset ? {
          ...selectedAsset,
          fileUrl: selectedAsset.fileUrl,
        } : null}
        open={detailOpen}
        onOpenChange={(open) => {
          setDetailOpen(open);
          if (!open) setSelectedAssetId(null);
        }}
        onUpdate={updateAsset}
        onReplaceFile={replaceFile}
        onRestoreVersion={restoreVersion}
        onDelete={removeAsset}
        onDownload={handleDownload}
      />

      {/* ── Image Lightbox ── */}
      <ImageLightbox
        src={lightboxSrc}
        alt={lightboxAlt}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {/* ── PDF Viewer (full-screen) ── */}
      <PdfViewer
        url={pdfViewerUrl ?? ""}
        title={pdfViewerTitle}
        open={pdfViewerOpen}
        onClose={() => {
          setPdfViewerOpen(false);
          setPdfViewerUrl(null);
        }}
      />

      {/* ── Confirm delete folder ── */}
      {/* ── Confirm delete asset ── */}
      {confirmDeleteAsset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px]">
          <div className={cn(
            "w-full max-w-[380px] rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-surface)] p-[var(--spacing-6)] shadow-lg",
            "animate-page-enter",
          )}>
            <h3 className="text-[16px] font-semibold text-[var(--color-text-primary)]">
              Delete asset?
            </h3>
            <p className="text-[13px] text-[var(--color-text-muted)] mt-[var(--spacing-2)]">
              <span className="font-medium text-[var(--color-text-primary)]">
                {allAssets.find((a) => a.id === confirmDeleteAsset)?.title ?? "This asset"}
              </span>
              {" "}will be permanently removed. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-[var(--spacing-2)] mt-[var(--spacing-4)]">
              <button
                onClick={() => setConfirmDeleteAsset(null)}
                className={cn(
                  "h-[32px] px-[var(--spacing-3)] text-[13px] font-medium",
                  "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]",
                  "rounded-[var(--radius-md)] transition-colors cursor-pointer",
                )}
              >
                Cancel
              </button>
              <button
                onClick={confirmAssetDelete}
                className={cn(
                  "h-[32px] px-[var(--spacing-4)] text-[13px] font-medium text-white",
                  "rounded-[var(--radius-md)]",
                  "bg-gradient-to-b from-red-500 to-red-700",
                  "hover:from-red-400 hover:to-red-600",
                  "transition-all cursor-pointer",
                )}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm delete folder ── */}
      {confirmDeleteFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px]">
          <div className={cn(
            "w-full max-w-[380px] rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-surface)] p-[var(--spacing-6)] shadow-lg",
            "animate-page-enter",
          )}>
            <h3 className="text-[16px] font-semibold text-[var(--color-text-primary)]">
              Delete folder?
            </h3>
            <p className="text-[13px] text-[var(--color-text-muted)] mt-[var(--spacing-2)]">
              The folder will be removed. Assets and subfolders inside it will be moved to the root level.
            </p>
            <div className="flex justify-end gap-[var(--spacing-2)] mt-[var(--spacing-4)]">
              <button
                onClick={() => setConfirmDeleteFolder(null)}
                className={cn(
                  "h-[32px] px-[var(--spacing-3)] text-[13px] font-medium",
                  "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]",
                  "rounded-[var(--radius-md)] transition-colors cursor-pointer",
                )}
              >
                Cancel
              </button>
              <button
                onClick={confirmFolderDelete}
                className={cn(
                  "h-[32px] px-[var(--spacing-4)] text-[13px] font-medium text-white",
                  "rounded-[var(--radius-md)]",
                  "bg-gradient-to-b from-red-500 to-red-700",
                  "hover:from-red-400 hover:to-red-600",
                  "transition-all cursor-pointer",
                )}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
